import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, startWith } from 'rxjs/operators';
import { SphereAppCategories } from '../constants/__app-categories';
import type { AppRoute } from '../types';
import { SphereAppCategory } from '../constants/app-categories';
import { of } from 'rxjs';
import { AuthorizationService } from '../auth';
import { LocalStorageService } from './local-storage/local-storage.service';
import { NavigationService } from './navigation.service';

export interface ISplitSphereApps {
    all: AppRoute[];
    favorites: AppRoute[];
    apps: AppRoute[];
}

@Injectable({
    providedIn: 'root'
})
export class SphereAppsService {
    public static FavoriteAppsSettingsKey = 'favorite-apps';
    private readonly authorization = inject(AuthorizationService);
    private readonly nav = inject(NavigationService);
    private readonly storage = inject(LocalStorageService);

    private readonly _favoriteAppIds = toSignal<string[]>(
        this.storage.watch<string[]>('portal', SphereAppsService.FavoriteAppsSettingsKey, { type: 'array', items: { type: 'string' } }).pipe(
            startWith([]),
            map(ids => {
                if (!ids || !Array.isArray(ids)) {
                    return [];
                }
                return ids as string[];
            }),
            catchError((err: Error) => {
                return of([] as string[]);
            })
        )
    );
    public readonly favoriteAppIds = computed(() => {
        if (this._favoriteAppIds() && Array.isArray(this._favoriteAppIds())) {
            return this._favoriteAppIds() as string[];
        }
        return [] as string[];
    });

    public readonly accessibleSphereApps = this.authorization.getAccessibleSphereApps([...this.nav.apps]);
    public readonly favoriteApps = computed(() => {
        return this.favoriteAppIds()
            .map(appName => this.accessibleSphereApps().find(app => app.data.id === appName))
            .filter(app => !!app && !!app.data);
    });
    public readonly appCategories = computed(() =>
        SphereAppCategories.filter(category => {
            try {
                return this.accessibleSphereApps().filter(app => app.data.categories.includes(category.category)).length > 0;
            } catch (e) {
                return false;
            }
        }).sort((prev, next) => {
            return prev > next ? -1 : 1;
        })
    );
    public readonly appsByCategory = computed(() => {
        const categoryMap = new Map<SphereAppCategory, AppRoute[]>();
        for (const app of this.accessibleSphereApps()) {
            for (const category of app.data.categories) {
                if (categoryMap.has(category)) {
                    const categoryRoutes = categoryMap.get(category) || [];
                    categoryMap.set(category, [...categoryRoutes, app]);
                } else {
                    categoryMap.set(category, [app]);
                }
            }
        }
        return categoryMap;
    });

    public readonly appsForSelectedCategory = (selectedCategory: SphereAppCategory | 'favorites' | null) =>
        computed<ISplitSphereApps>(() => {
            if (!selectedCategory) {
                return this.splitFavoriteApps(this.accessibleSphereApps());
            }
            try {
                if (selectedCategory === 'favorites') {
                    return { favorites: this.favoriteApps() || [], apps: [], all: this.favoriteApps() || [] } as ISplitSphereApps;
                } else {
                    return this.splitFavoriteApps(this.appsByCategory().get(selectedCategory as SphereAppCategory) || []);
                }
            } catch (e) {
                console.error(e);
                return { apps: [], favorites: [], all: [] } as ISplitSphereApps;
            }
        });

    public splitFavoriteApps(_apps: AppRoute[]): ISplitSphereApps {
        const apps = [],
            favorites = [];
        for (const app of _apps) {
            if (this.favoriteApps().findIndex(_app => _app?.data.id === app.data.id) >= 0) {
                favorites.push(app);
            } else {
                apps.push(app);
            }
        }
        return { apps, favorites, all: _apps };
    }

    public addFavoriteApp(app: AppRoute) {
        const favoriteApps: string[] = [...this.favoriteAppIds(), app.data.id];
        return this.storage
            .set('portal', SphereAppsService.FavoriteAppsSettingsKey, favoriteApps, { type: 'array', items: { type: 'string' } })
            .subscribe();
    }

    public removeFavoriteApp(app: AppRoute) {
        const favoriteApps: string[] = this.favoriteAppIds().filter(id => id !== app.data.id);
        return this.storage
            .set('portal', SphereAppsService.FavoriteAppsSettingsKey, favoriteApps, { type: 'array', items: { type: 'string' } })
            .subscribe();
    }
}
